/**
 * OrganizatorYetkileriKarti.jsx — bir organizatör firmanın portal ayarları (yalnızca personel).
 * Backlog #313, backend #316 (organizator_ayar, V73).
 *
 * SAKA, organizatör firma başına açar — #446 / #447 (2026-10-17): görünüm artık TEK ANAHTAR DEĞİL, ALAN BAŞINA KADEME:
 * Standlar, Firmalar, Talepler ve borç, Projeler, Kişiler, Salonlar ve planlar. Kademe Yok (diğer firmaların hiçbir şeyini görmez) <
 * İzleme (listeleri ve durumları görür, hiçbir şeyi değiştirmez) < Denetim (İzleme + SAKA'nın açtığı eylemler). Eylemler (stand ekle /
 * düzenle / sil, firma ekle / eşle / sil, taşeron ekle, standa talep ekle / iptal, borç yönetimi) ilgili alanın altında, o alanın anahtarlarıdır;
 * yalnızca alan Denetim kademesindeyse işe yarar. Kayıt yok = Yok / kapalı.
 * Bir eylem için hepsi birden gerekir: firma o fuarda Organizatör, alan Denetim, girişin portal rolü eylemi içerir ve buradaki anahtar açık.
 * Firma girişi bu kartı görmez; PUT personel dışına 403.
 * WAS (until #447): tek "Fuar geneli görünüm" anahtarı (FUAR_GORUNUMU) ve eylemlerin düz listesi.
 *
 * ⚠️ Varsayılan KAPALI: firma_to_fuar tip 2'de hizmet firmaları da var (DHL, zemin, temizlik, güvenlik — #326); bir alanı açmadan önce
 * firmanın gerçekten fuarın organizatörü olduğundan emin olun.
 *
 * @param {number}   firmaId
 * @param {Function} onChanged `(mesaj)`
 */
import React, { useEffect, useState } from "react";
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Input, Label, Spinner, Table,
} from "reactstrap";
import { Megaphone } from "lucide-react";
// WAS (until #447): import { getOrganizatorAyar, setOrganizatorAyar, ORGANIZATOR_AYAR } from "../../services/portalService";
import { getOrganizatorAyar, setOrganizatorAyar, KADEME } from "../../services/portalService";
// #456 (2026-10-17): the organizer's payment link (the free-kW notice); staff enter it here for any of the company's fairs, or for all of them
import OdemeBaglantisiKarti from "../Talep/OdemeBaglantisiKarti";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../i18n";

const KADEMELER = [KADEME.NONE, KADEME.MONITOR, KADEME.AUDIT];

function trTarihSaat(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" });
}

export default function OrganizatorYetkileriKarti({ firmaId, onChanged }) {
  const { t: tx, i18n: dil } = useTranslation("components");
  const [veri, setVeri] = useState(null);
  const [taslak, setTaslak] = useState({});
  // #447: the draft tier of each area (alan -> NONE / MONITOR / AUDIT)
  const [kademeTaslak, setKademeTaslak] = useState({});
  const [not, setNot] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const out = await getOrganizatorAyar(firmaId);
        if (ignore) return;
        setVeri(out);
        setTaslak(Object.fromEntries((out?.ayarlar ?? []).map((a) => [a.kod, !!a.izinli])));
        setKademeTaslak(Object.fromEntries((out?.alanlar ?? []).map((a) => [a.alan, a.kademe])));
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [firmaId]);

  const degisenler = (veri?.ayarlar ?? []).filter((a) => !!a.izinli !== !!taslak[a.kod]);
  const degisenAlanlar = (veri?.alanlar ?? []).filter((a) => a.kademe !== (kademeTaslak[a.alan] ?? a.kademe));
  const degisenSayisi = degisenler.length + degisenAlanlar.length;

  async function kaydet() {
    setSaving(true);
    setError(null);
    try {
      const ayarlar = Object.fromEntries(degisenler.map((a) => [a.kod, !!taslak[a.kod]]));
      const alanlar = Object.fromEntries(degisenAlanlar.map((a) => [a.alan, kademeTaslak[a.alan]]));
      // WAS (until #447): const out = await setOrganizatorAyar(firmaId, ayarlar, not.trim());
      const out = await setOrganizatorAyar(firmaId, ayarlar, not.trim(), alanlar);
      setVeri(out);
      setTaslak(Object.fromEntries((out?.ayarlar ?? []).map((a) => [a.kod, !!a.izinli])));
      setKademeTaslak(Object.fromEntries((out?.alanlar ?? []).map((a) => [a.alan, a.kademe])));
      setNot("");
      onChanged?.(out?.uyari || tx("organizatorYetkileriKarti.organizatorYetkileriKaydedildi"));
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading && !veri) {
    return <Card className="mb-3"><CardBody><Spinner size="sm" color="primary" /></CardBody></Card>;
  }
  if (!veri) {
    return error ? <Alert color="danger" className="small">{error}</Alert> : null;
  }

  const organizator = Number(veri.organizatorFuarSayisi) > 0;
  // WAS (until #456): return ( <Card className="mb-3"> ... </Card> );  -- the card is followed by the payment link card now
  return (
    <>
    <Card className="mb-3">
      <CardHeader className="bg-transparent d-flex align-items-center gap-2">
        <Megaphone size={16} className="text-muted" />
        <strong className="flex-grow-1">{tx("organizatorYetkileriKarti.organizatorYetkileri")}</strong>
        {organizator
          ? <Badge color="primary">{veri.organizatorFuarSayisi}{" "}{tx("organizatorYetkileriKarti.fuardaOrganizator")}</Badge>
          : <Badge color="secondary">{tx("organizatorYetkileriKarti.organizatorDegil")}</Badge>}
      </CardHeader>
      <CardBody>
        {error && <Alert color="danger" className="py-2 small mb-3">{error}</Alert>}
        {!organizator && (
          <Alert color="light" className="py-2 small border">
            {tx("organizatorYetkileriKarti.buFirmaHicbirFuardaOrganizator")}
          </Alert>
        )}
        <p className="text-muted small">
          {tx("organizatorYetkileriKarti.birEylemIcinFirmaO")}
        </p>
        {/* #447: one block per AREA: its tier, then the action flags that belong to it (they only work at Denetim) */}
        <div className="table-responsive" data-testid="organizator-alanlar">
          <Table size="sm" className="align-middle mb-3">
            <thead className="table-light">
              <tr><th>{tx("organizatorYetkileriKarti.alan")}</th><th style={{ width: 200 }}>{tx("organizatorYetkileriKarti.kademe.baslik")}</th><th>{tx("organizatorYetkileriKarti.gerekenModul")}</th><th>{tx("organizatorYetkileriKarti.sonDegisiklik")}</th></tr>
            </thead>
            <tbody>
              {(veri.alanlar ?? []).map((al) => {
                const kademe = kademeTaslak[al.alan] ?? al.kademe;
                const bayraklar = veri.ayarlar.filter((x) => x.alan === al.alan);
                return (
                  <React.Fragment key={al.alan}>
                    <tr className="table-light" data-alan={al.alan}>
                      <td className="fw-semibold">{(dil.language || '').startsWith('en') ? al.adEn || al.ad : al.ad}<div className="text-muted font-monospace" style={{ fontSize: 11 }}>{al.alan}</div></td>
                      <td>
                        <Input type="select" bsSize="sm" id={`ak-${al.alan}`} value={kademe} disabled={saving} aria-label={al.ad}
                               onChange={(e) => setKademeTaslak((t) => ({ ...t, [al.alan]: e.target.value }))}>
                          {KADEMELER.map((k) => <option key={k} value={k}>{tx(`organizatorYetkileriKarti.kademe.${k}`)}</option>)}
                        </Input>
                      </td>
                      <td className="small text-muted">{tx(`organizatorYetkileriKarti.kademeAciklama.${kademe}`)}</td>
                      <td className="small text-muted">
                        {al.kayitVar
                          ? [al.ayarlayanAdSoyad || (al.ayarlayanUserId != null ? `#${al.ayarlayanUserId}` : null),
                            trTarihSaat(al.tarih)].filter(Boolean).join(" · ")
                          : tx("organizatorYetkileriKarti.kayitYok")}
                        {al.not && <div className="fst-italic">{al.not}</div>}
                      </td>
                    </tr>
                    {bayraklar.map((x) => (
                      <tr key={x.kod}>
                        <td className="ps-4">
                          <Label for={`oa-${x.kod}`} className="mb-0 small">{x.ad}</Label>
                          <div className="text-muted font-monospace" style={{ fontSize: 11 }}>{x.kod}</div>
                        </td>
                        <td>
                          <Input type="switch" role="switch" id={`oa-${x.kod}`} checked={!!taslak[x.kod]} disabled={saving}
                                 onChange={(e) => setTaslak((t) => ({ ...t, [x.kod]: e.target.checked }))} />
                        </td>
                        {/* WAS (until #419): <td className="small text-muted">{a.gerekenRolIzni || "—"}</td> -- the role permission, retired */}
                        <td className="small text-muted">
                          {x.gerekenModul
                            ? `${tx(`ayarlar:portalArayuzleri.modul.${x.gerekenModul}`, { defaultValue: x.gerekenModul })} ≥ ${tx(`ayarlar:portalArayuzleri.seviye.${x.gerekenSeviye}`, { defaultValue: x.gerekenSeviye })}`
                            : "—"}
                          {kademe !== KADEME.AUDIT && <div className="text-warning-emphasis" data-testid="bayrak-denetim-gerek">{tx("organizatorYetkileriKarti.denetimGerekir")}</div>}
                        </td>
                        <td className="small text-muted">
                          {x.kayitVar
                            ? [x.ayarlayanAdSoyad || (x.ayarlayanUserId != null ? `#${x.ayarlayanUserId}` : null),
                              trTarihSaat(x.tarih)].filter(Boolean).join(" · ")
                            : tx("organizatorYetkileriKarti.kayitYok")}
                          {x.not && <div className="fst-italic">{x.not}</div>}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
        {/* WAS (until #447): a flat table, one switch per setting, FUAR_GORUNUMU first; the previous version is in git history */}
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <Input bsSize="sm" style={{ maxWidth: 420 }} placeholder={tx("organizatorYetkileriKarti.notDegisenSatirlaraYazilir")} maxLength={500}
                 value={not} disabled={saving} onChange={(e) => setNot(e.target.value)} />
          <Button color="primary" size="sm" disabled={saving || degisenSayisi === 0} onClick={kaydet}>
            {/* WAS (until #447): `Kaydet${degisenler.length ? ` (${degisenler.length})` : ""}` */}
            {saving ? <Spinner size="sm" /> : `${tx("organizatorYetkileriKarti.kaydet")}${degisenSayisi ? ` (${degisenSayisi})` : ""}`}
          </Button>
        </div>
      </CardBody>
    </Card>
    {/* #456: staff enter the organizer's payment link here (any fair, or every fair); an organizer enters its own in its panel where SAKA allows it above */}
    {organizator && <OdemeBaglantisiKarti firmaId={firmaId} personel duzenleyebilir onChanged={onChanged} />}
    </>
  );
}
